import { useState, useEffect, useCallback } from 'react';
import { useUser } from '../../context/UserContext';
import { supabase } from '../../lib/supabase';
import { Eye, EyeOff } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Card, Button, Input, Dropdown, SidePanel, KebabMenu, Icon } from '../../components/ui';

interface ApiKey {
  id: string;
  service_name: string;
  description: string | null;
  api_key: string;
  created_at: string;
  updated_at: string;
}

const SUPPORTED_SERVICES = ['Claude', 'ChatGPT', 'Gemini', 'Deepseek', 'Qwen'];

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export default function APIKeysPanel() {
  const { user } = useUser();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddingKey, setIsAddingKey] = useState(false);
  const [editingKeyId, setEditingKeyId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showKey, setShowKey] = useState<{ [key: string]: boolean }>({});
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    service_name: '',
    api_key: '',
    description: '',
  });

  const fetchApiKeys = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setError('You must be logged in to view API keys');
        return;
      }

      const { data, error: fetchError } = await supabase.rpc('get_own_api_keys').order('service_name');

      if (fetchError) throw fetchError;

      setApiKeys(data || []);
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to load API keys. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, [user, fetchApiKeys]);

  const maskApiKey = (key: string): string => {
    if (!key) return '';
    if (key.length <= 4) return '••••';
    return '•'.repeat(Math.min(key.length - 4, 20)) + key.slice(-4);
  };

  const toggleShowKey = (keyId: string) => {
    setShowKey((prev) => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const handleCopy = (key: ApiKey) => {
    navigator.clipboard.writeText(key.api_key).catch(() => {});
    setCopiedKeyId(key.id);
    setTimeout(() => setCopiedKeyId((current) => (current === key.id ? null : current)), 2000);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!formData.service_name.trim() || !formData.api_key.trim()) {
      setError('Service name and API key are required');
      return;
    }

    setIsSubmitting(true);

    try {
      if (editingKeyId) {
        const { error: updateError } = await supabase
          .from('user_api_keys')
          .update({
            service_name: formData.service_name,
            api_key: formData.api_key,
            description: formData.description || null,
          })
          .eq('id', editingKeyId);

        if (updateError) {
          if (updateError.message.includes('permission denied for function')) {
            throw new Error('API key encryption failed. Please try again later or contact support.');
          }
          throw updateError;
        }
      } else {
        if (!user?.id) throw new Error('Not signed in');
        const { error: insertError } = await supabase.from('user_api_keys').insert({
          user_id: user.id,
          service_name: formData.service_name,
          api_key: formData.api_key,
          description: formData.description || null,
        });

        if (insertError) throw insertError;
      }

      resetForm();
      fetchApiKeys();
    } catch (err) {
      console.error('Error saving API key:', err);
      setError('Failed to save API key. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ service_name: '', api_key: '', description: '' });
    setIsAddingKey(false);
    setEditingKeyId(null);
  };

  const handleEdit = (key: ApiKey) => {
    setFormData({ service_name: key.service_name, api_key: '', description: key.description || '' });
    setEditingKeyId(key.id);
    setIsAddingKey(true);
  };

  const handleDelete = async (keyId: string) => {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase.from('user_api_keys').delete().eq('id', keyId);
      if (error) throw error;
      fetchApiKeys();
    } catch (err) {
      console.error('Error deleting API key:', err);
      setError('Failed to delete API key. Please try again.');
    }
  };

  return (
    <>
      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            className="pressable"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--text-sm)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      <Card
        eyebrow="API Keys"
        title={`${apiKeys.length} active`}
        actions={
          <Button
            size="sm"
            variant="secondary"
            icon="plus"
            onClick={() => {
              resetForm();
              setIsAddingKey(true);
            }}
          >
            Add Key
          </Button>
        }
      >
        {isLoading ? (
          <div className="flex flex-col items-center" style={{ padding: 'var(--space-8) 0' }}>
            <div
              className="animate-spin"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: 'var(--radius-pill)',
                border: '3px solid var(--border-hairline)',
                borderTopColor: 'var(--accent)',
                marginBottom: 'var(--space-3)',
              }}
            />
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading...</p>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="flex flex-col items-center" style={{ padding: 'var(--space-8) 0' }}>
            <FontAwesomeIcon
              icon={faKey}
              style={{ fontSize: '22px', color: 'var(--text-faint)', marginBottom: 'var(--space-3)' }}
            />
            <p style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
              No API keys yet. Keys connect Cockpit to third-party AI services.
            </p>
            <Button size="sm" icon="plus" onClick={() => setIsAddingKey(true)}>
              Add Key
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className="hover-wash"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px',
                  margin: '0 -10px',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <FontAwesomeIcon
                  icon={faKey}
                  style={{ fontSize: '12px', width: '16px', color: 'var(--text-faint)', flexShrink: 0 }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                    {key.service_name}
                    {key.description ? (
                      <span style={{ color: 'var(--text-faint)' }}> · {key.description}</span>
                    ) : null}
                  </span>
                  <span
                    className="t-num"
                    style={{
                      display: 'block',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-faint)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {showKey[key.id] ? key.api_key : maskApiKey(key.api_key)} · updated{' '}
                    {formatDate(key.updated_at)}
                  </span>
                </span>
                <button
                  className="pressable hover-wash"
                  title={showKey[key.id] ? 'Hide key' : 'Show key'}
                  onClick={() => toggleShowKey(key.id)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-faint)',
                    cursor: 'pointer',
                    padding: '5px 6px',
                    borderRadius: 'var(--radius-sm)',
                    display: 'inline-flex',
                  }}
                >
                  {showKey[key.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  className="pressable hover-wash"
                  title={copiedKeyId === key.id ? 'Copied' : 'Copy key'}
                  onClick={() => handleCopy(key)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: copiedKeyId === key.id ? 'var(--status-up)' : 'var(--text-faint)',
                    cursor: 'pointer',
                    padding: '5px 6px',
                    borderRadius: 'var(--radius-sm)',
                    display: 'inline-flex',
                    transition: 'color var(--dur-base) var(--ease-out)',
                  }}
                >
                  <Icon name={copiedKeyId === key.id ? 'check' : 'copy'} size={14} />
                </button>
                <KebabMenu
                  label="Key actions"
                  actions={[
                    { label: 'Edit Key', icon: faPen, onClick: () => handleEdit(key) },
                    { label: 'Delete', icon: faTrash, danger: true, onClick: () => handleDelete(key.id) },
                  ]}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <p
        style={{
          margin: 'var(--space-4) 0 0',
          fontSize: 'var(--text-xs)',
          color: 'var(--text-faint)',
          lineHeight: 1.6,
        }}
      >
        Keys are encrypted at rest with column-level encryption. Use them only inside Supabase Edge
        Functions, never in frontend code.
      </p>

      <SidePanel
        open={isAddingKey}
        onClose={resetForm}
        eyebrow="API Keys"
        title={editingKeyId ? 'Edit Key' : 'Add Key'}
        width={400}
        footer={
          <>
            <Button icon="check" disabled={isSubmitting} onClick={handleSubmit}>
              {isSubmitting ? 'Saving...' : 'Save Key'}
            </Button>
            <Button variant="ghost" disabled={isSubmitting} onClick={resetForm}>
              Cancel
            </Button>
          </>
        }
      >
        <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {error && (
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--danger-500)' }}>{error}</p>
          )}
          <Dropdown
            label="Service"
            options={SUPPORTED_SERVICES}
            value={formData.service_name}
            onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
            placeholder="Select a service..."
          />
          <Input
            label={editingKeyId ? 'API Key (blank keeps current)' : 'API Key'}
            type="password"
            icon="lock"
            value={formData.api_key}
            onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
            placeholder="Enter API key..."
          />
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span className="t-eyebrow">Description</span>
            <textarea
              className="ck-textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What is this key used for?"
            />
          </label>
        </div>
      </SidePanel>
    </>
  );
}
